/**
 * **DOGFIGHT A SQUADRON.** Your flight against a named enemy squadron's flight.
 *
 * The manual's own blurb is *"Engage an enemy flight of fighters and try to
 * clear them out of the sky. Just remember that they're trying to do the same to
 * your flight."* (`manual/04-gameplay.md`), and its worked example ends *"After
 * you've destroyed or chased off all the enemy fighters, you are to return to
 * your aerodrome"* — which is the two objectives below, in that order.
 *
 * **There is no squadron picker, and this port does not invent one.**
 * docs/game/missions.md reads `PS.EXE` as drawing this mode "from the
 * Elite/Ordinary/Generic squadron rosters", which is easy to mistake for a list
 * the player chooses from. It is not: the only `Pick a Squadron:` string in the
 * binary sits in the *career* `REQUEST TRANSFER` cluster, between "before the %s
 * will accept a transfer request" and "You have been transferred to %q". This
 * mode's own string cluster has `SELECT LEADER:` / `SELECT PILOT:` and two
 * `SELECT AIRCRAFT:` and no squadron list at all, and the Situation prose
 * carries ` Aerodrome`, `It's believed that ` and ` is leading them. ` — the
 * enemy unit is *narrated*, never selected. So the squadron is drawn here, and
 * the briefing names it.
 *
 * **What the squadron decides is the quality of the opposition**, and that is
 * decoded rather than invented: `ELITE.DAT`/`ORDINARY.DAT` carry a
 * novice/regular/veteran percentage mix per squadron per month
 * (`campaign/squadron-strength.ts`), and elite units really do fly at a
 * different rung of it — 59% veterans against an ordinary squadron's 36%. Meet
 * J.G.1 and nine cockpits in ten hold a veteran; meet Jasta 5 and it is one in
 * two. The same table says which aeroplane the unit flew that era and which
 * famous ace was on strength, so an ace who is there leads the flight, at the
 * `ace` tier, on his own historical mount.
 *
 * **Flight size stops being decorative here.** `FORMATION` and `TOTAL AIRCRAFT
 * IN FLIGHT` were drawn on the Flight Assignments screen and wired to nothing —
 * `missions-screens.ts` left them as a named extension point. This mission is
 * what needs them: `PS.EXE`'s count vocabulary is exactly `one`/`two`/`three`/
 * `four` and nothing longer, in every binary, so both flights are one to four
 * aeroplanes and the fight is at most four against four.
 */

import type { Vec3 } from "../flight/math3d.ts";
import type { AiSkill } from "../combat/skill.ts";
import { drawPilotSkill, squadronStrength, type SquadronStrength } from "../campaign/squadron-strength.ts";
import { SQUADRONS, type Squadron } from "../campaign/squadrons.ts";
import { buildFlight, briefingLines, aerodromeStartLine, climbFrom, waypoint } from "./mission-build.ts";
import { defaultFormation, formationStation, isFormationLegal, type Formation } from "./formations.ts";
import { missionContext, type MissionContext, type MissionOptions } from "./mission-options.ts";
import { aceById, type FamousAce } from "./mission-roster.ts";
import { assembleMission } from "./mission-assemble.ts";
import { homeAerodromeAt } from "./generators-scramble.ts";
import { MAX_FLIGHT_AIRCRAFT } from "./raid-parts.ts";
import type { MissionFlight, MissionSpec } from "./mission-spec.ts";
import type { MissionObjective } from "./objectives.ts";

/** The merge height. Same 1,200 m the ace duel uses, and for the same reason — it is where the AI was tuned. */
const FIGHT_ALTITUDE_M = 1200;
/** How far apart the two flights start, along the run-in axis. */
const MERGE_SEPARATION_M = 2600;

/**
 * The smallest flight the enemy squadron will put up. The grid slot refused
 * with *"and a flight of more than two"*, and that is the reason: Dogfight a
 * Famous Ace already generates a pair against a pair, so a squadron fight that
 * drew two would be the duel with a different title. Three is the floor for the
 * squadron's flight, and the player's own may still be two — being outnumbered
 * by a Jasta is the mission.
 */
export const MIN_SQUADRON_FLIGHT = 3;

/**
 * How big the two flights are when nobody has said. **One draw, both sides** —
 * three against three or four against four, never a generated edge in numbers
 * for either.
 *
 * That is a deliberate correction, and the censuses are why. The first cut drew
 * the two sizes independently, which handed the squadron the larger flight more
 * often than not; against the decoded veteran share that killed the player in
 * 21 of 24 sorties, where the port's other air-to-air missions kill him in 6 to
 * 13. Being outnumbered is something the player can *choose* here — `flightSize`
 * and `enemyFlightSize` are both his to set, exactly as the manual describes —
 * but a mission that quietly generates the disadvantage every time is a port
 * decision the original never made. What separates the two flights by default is
 * quality, which is the decoded fact, not headcount, which is not.
 */
const FLIGHT_SIZES: readonly number[] = [3, 3, 4, 4];

function clampFlightSize(requested: number | undefined, fallback: number): number {
  if (requested === undefined || !Number.isFinite(requested)) return fallback;
  return Math.max(1, Math.min(MAX_FLIGHT_AIRCRAFT, Math.floor(requested)));
}

/** The enemy squadrons that were on strength in this era, on the side the player is not flying for. */
export function squadronsInPlay(context: MissionContext): readonly Squadron[] {
  return SQUADRONS.filter(
    (unit) =>
      unit.nation === context.enemyNation &&
      unit.role === "fighter" &&
      squadronStrength(unit.id, context.era) !== undefined,
  );
}

interface Opposition {
  readonly squadron: Squadron;
  readonly strength: SquadronStrength;
  /** The famous ace leading them, when the squadron had one on strength that era. */
  readonly ace: FamousAce | undefined;
}

function opposition(context: MissionContext, options: MissionOptions): Opposition {
  const inPlay = squadronsInPlay(context);
  const named = options.squadronId ? inPlay.find((unit) => unit.id === options.squadronId) : undefined;
  const squadron = named ?? context.draw.pick(inPlay);
  const strength = squadronStrength(squadron.id, context.era);
  if (!strength) throw new Error(`no ${context.era} strength for squadron "${squadron.id}"`);
  // The months name the aces in order; the first is the one who leads.
  const aceId = strength.aces[0];
  return { squadron, strength, ace: aceId ? aceById(aceId) : undefined };
}

interface OwnSection {
  readonly context: MissionContext;
  readonly size: number;
  readonly formation: Formation;
  readonly position: Vec3;
  readonly onGround: boolean;
  readonly waypoints: readonly ReturnType<typeof waypoint>[];
  readonly ammunition: MissionOptions["ammunition"];
  /** The player's own unit — his wingmen are rolled from its decoded mix, exactly as the enemy's are from theirs. */
  readonly strength: SquadronStrength;
}

/**
 * The player's flight: him at the front, wingmen on their formation stations.
 *
 * **His wingmen are drawn from a real squadron's decoded mix, not from a
 * hand-written ladder**, for two reasons. The manual says they are generated
 * rather than picked — *"The pilots that are assigned to fly with you, and
 * their skill levels, are based upon the historical context and random
 * generation of the mission you are flying"* — and the original's own Flight
 * Assignments capture shows a three-aeroplane flight whose other two rows both
 * read `Veteran Pilot` (`docs/game/ui-reference/index.md`). A ladder of
 * `regular` then `novice`, which is what the raid family gives its wingmen,
 * would have contradicted that capture and sent the player into a Jasta with
 * two beginners behind him. Rolling both sides off `ELITE.DAT`/`ORDINARY.DAT`
 * makes the fight symmetric in *kind*: your squadron against theirs, with the
 * decoded difference between an ordinary unit and an elite one doing the work.
 */
function ownFlight(section: OwnSection): MissionFlight[] {
  const { context, formation, position, waypoints } = section;
  return Array.from({ length: section.size }, (_unused, index) =>
    buildFlight({
      id: index === 0 ? "player" : `wingman-${index}`,
      side: "friendly",
      aircraft: context.playerAircraft,
      callsign: index === 0 ? "You" : `Wingman ${index}`,
      position: formationStation(formation, index, position, 0),
      headingRad: 0,
      onGround: section.onGround ? true : undefined,
      // The player's own seat takes no `skill`, which is what makes it his
      // (`mission-build.ts`).
      ...(index === 0 ? {} : { skill: drawPilotSkill(context.draw, section.strength) as AiSkill }),
      ammunition: section.ammunition,
      waypoints: waypoints.length > 0 ? waypoints : undefined,
    }),
  );
}

/**
 * The squadron the player's own wingmen come from: one of his nation's ordinary
 * fighter units that was on strength this era. Ordinary rather than elite
 * because a single-mission pilot is not on an elite posting — the manual is
 * explicit that those are earned in a career (*"you may attain enough clout to
 * fly with J.G.1"*), so putting him in one for a one-off sortie would hand him a
 * privilege the game makes you work for.
 */
function ownStrength(context: MissionContext): SquadronStrength {
  const home = SQUADRONS.filter(
    (unit) =>
      unit.nation === context.nation &&
      unit.role === "fighter" &&
      !unit.elite &&
      squadronStrength(unit.id, context.era) !== undefined,
  );
  const unit = context.draw.pick(home);
  const strength = squadronStrength(unit.id, context.era);
  if (!strength) throw new Error(`no ${context.era} strength for squadron "${unit.id}"`);
  return strength;
}

/**
 * The enemy squadron's flight. The leader is the resident ace at the `ace` tier
 * on his own mount when the unit had one; otherwise he is the best pilot the
 * squadron's own mix produced, which is the manual's *"an anonymous pilot of any
 * quality"*. Everyone behind him is rolled from the decoded percentages.
 */
function enemyFlight(
  context: MissionContext,
  enemy: Opposition,
  size: number,
  formation: Formation,
  position: Vec3,
  aircraft: SquadronStrength["aircraft"],
  aceAircraft: SquadronStrength["aircraft"],
): MissionFlight[] {
  return Array.from({ length: size }, (_unused, index) => {
    const leader = index === 0;
    const ace = leader ? enemy.ace : undefined;
    return buildFlight({
      id: `hostile-${index + 1}`,
      side: "hostile",
      aircraft: ace ? aceAircraft : aircraft,
      callsign: ace ? ace.name : `${enemy.squadron.name} ${index + 1}`,
      position: formationStation(formation, index, position, Math.PI),
      headingRad: Math.PI,
      skill: ace ? "ace" : drawPilotSkill(context.draw, enemy.strength),
    });
  });
}

/** `PS.EXE`'s `SITUATION` prose for this mode, from its own fragments. */
function situationLines(enemy: Opposition, enemyCount: number): string[] {
  const lines = [
    `A flight of ${enemyCount} from ${enemy.squadron.name} is up out of ${enemy.squadron.aerodrome} Aerodrome.`,
  ];
  if (enemy.ace) lines.push(`It's believed that ${enemy.ace.name} is leading them.`);
  return lines;
}

/**
 * The tier `PS.EXE`'s briefing template calls the opposition by — its `%z` slot,
 * whose only three values are `a novice pilot` / `a regular pilot` / `a veteran
 * pilot`. It is read off the squadron's *own* decoded mix rather than off the
 * elite flag, because the two do not always agree: Jasta 27 is an ordinary
 * squadron that still fields 50% veterans in 1918, and calling that flight
 * "regular" because of which file it lives in would be the briefing lying about
 * what is coming.
 */
function dominantSkill(strength: SquadronStrength): string {
  const { novicePct, regularPct, veteranPct } = strength;
  if (veteranPct >= regularPct && veteranPct >= novicePct) return "veteran";
  return regularPct >= novicePct ? "regular" : "novice";
}

/**
 * The line that tells the player what he is actually walking into. The
 * percentages are the squadron's own decoded mix, and saying them plainly is
 * the difference between "an enemy flight" and "nine cockpits in ten hold a
 * veteran".
 */
function strengthLine(enemy: Opposition): string {
  const { strength } = enemy;
  const tier = enemy.squadron.elite ? "an elite squadron" : "an ordinary squadron";
  return `${enemy.squadron.name} is ${tier}: ${strength.veteranPct}% of its pilots are veterans, ${strength.regularPct}% regulars, ${strength.novicePct}% novices.`;
}

/** How big each flight is, and what shape the player's is in. */
interface Sizing {
  readonly ownSize: number;
  readonly enemySize: number;
  readonly formation: Formation;
  readonly enemyFormation: Formation;
}

function sizing(context: MissionContext, options: MissionOptions): Sizing {
  const drawn = context.draw.pick(FLIGHT_SIZES);
  const ownSize = clampFlightSize(options.flightSize, drawn);
  const enemySize = clampFlightSize(options.enemyFlightSize, drawn);
  const requested = options.formation;
  return {
    ownSize,
    enemySize,
    formation: requested && isFormationLegal(requested, ownSize) ? requested : defaultFormation(ownSize),
    enemyFormation: defaultFormation(enemySize),
  };
}

/** Where the two flights start, and the legs the player's is given. */
interface Merge {
  readonly enemyAltitude: number;
  readonly start: Vec3;
  readonly enemyStart: Vec3;
  readonly contact: ReturnType<typeof waypoint>;
  readonly home: ReturnType<typeof waypoint>;
}

function mergeGeometry(context: MissionContext): Merge {
  const { draw } = context;
  // Height is drawn *either way*, unlike the ace duel's, which always stacks the
  // ace above the player. One aeroplane conceding the height to one opponent is
  // a duel's opening move; four conceding it to four is a massacre, and the
  // census showed exactly that — the player died in 22 of 24 sorties before the
  // advantage was allowed to fall his way half the time.
  const enemyAltitude = FIGHT_ALTITUDE_M + draw.side() * draw.int(0, 4) * 120;
  const drift = draw.between(-300, 300);
  return {
    enemyAltitude,
    start: { x: 0, y: FIGHT_ALTITUDE_M, z: MERGE_SEPARATION_M / 2 },
    enemyStart: { x: drift, y: enemyAltitude, z: -MERGE_SEPARATION_M / 2 },
    contact: waypoint({ x: drift, y: FIGHT_ALTITUDE_M, z: -MERGE_SEPARATION_M / 4 }, "CONTACT"),
    // The way home, always — the manual's *"you are to return to your
    // aerodrome"* is the second objective, and without a leg to fly, a flight
    // that has cleared the sky simply loiters over the wreckage and never
    // satisfies it.
    home: waypoint({ x: 0, y: FIGHT_ALTITUDE_M - 200, z: MERGE_SEPARATION_M }, "HOME"),
  };
}

function squadronObjectives(
  enemy: Opposition,
  enemySize: number,
  hostileIds: readonly string[],
): readonly MissionObjective[] {
  return [
    {
      kind: "destroyAircraft",
      id: "clear-the-sky",
      label: `Clear ${enemy.squadron.name} out of the sky`,
      role: "primary",
      count: enemySize,
      flightIds: hostileIds,
    },
    {
      kind: "returnToBase",
      id: "rtb",
      label: "Return across your own lines",
      role: "primary",
      flightId: "player",
    },
  ];
}

export function generateSquadronFight(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "dogfight-squadron");
  const enemy = opposition(context, options);
  const { ownSize, enemySize, formation, enemyFormation } = sizing(context, options);

  // The ace keeps his historical mount unless the player pinned an enemy type,
  // exactly as Dogfight a Famous Ace handles the manual's "put the Red Baron in
  // a Camel" example; everyone else flies what the squadron flew that era.
  const squadronAircraft = options.enemyAircraft ?? enemy.strength.aircraft;
  const aceAircraft = options.enemyAircraft ?? enemy.ace?.aircraft ?? enemy.strength.aircraft;

  const { enemyAltitude, start, enemyStart, contact, home } = mergeGeometry(context);
  const base = context.aerodromeStart ? homeAerodromeAt(context) : undefined;

  const flights: MissionFlight[] = [
    ...ownFlight({
      context,
      size: ownSize,
      formation,
      position: base ? base.position : start,
      onGround: base !== undefined,
      waypoints: base ? [climbFrom(base, FIGHT_ALTITUDE_M), contact, home] : [contact, home],
      ammunition: options.ammunition,
      strength: ownStrength(context),
    }),
    ...enemyFlight(context, enemy, enemySize, enemyFormation, enemyStart, squadronAircraft, aceAircraft),
  ];

  const objectives = squadronObjectives(
    enemy,
    enemySize,
    flights.filter((flight) => flight.side === "hostile").map((flight) => flight.id),
  );

  return assembleMission(context, {
    catalogId: "dogfight-squadron",
    type: "dogfight",
    title: `Squadron fight: ${enemy.squadron.name}`,
    flights,
    objectives,
    homeBase: base,
    briefing: [
      ...(base ? [aerodromeStartLine(base)] : []),
      ...situationLines(enemy, enemySize),
      ...briefingLines({
        flightSize: ownSize,
        ownAircraft: context.playerAircraft,
        enemyCount: enemySize,
        enemySkill: dominantSkill(enemy.strength),
        enemyNation: context.enemyNation,
        enemyLeader: enemy.ace ? enemy.ace.name : `an unnamed ${enemy.squadron.name} leader`,
        enemyAircraft: squadronAircraft,
        region: context.region,
        ownAltitudeM: base ? 0 : FIGHT_ALTITUDE_M,
        enemyAltitudeM: enemyAltitude,
        weather: context.weather,
      }),
      strengthLine(enemy),
      ...(enemy.ace ? [enemy.ace.note] : []),
    ],
    portNotes: [
      "The formation words and the one-to-four flight sizes are `PS.EXE`'s own; where each formation puts an aeroplane is port-side, because no decoded file carries station offsets — only the Flight Assignments thumbnail, which this port has no capture of (`mission/formations.ts`).",
      "\"Chased off\" is not modelled: the manual's win is \"destroyed or chased off all the enemy fighters\", and this port counts only the ones shot down. An enemy who disengages and runs still has to be caught.",
    ],
  });
}
